import type { FormikErrors, FormikProps } from 'formik'

import { FlowPage, FormItem } from '@monthio/design-system'
import { TextInputField } from '@monthio/react-components'
import { useField } from 'formik'
import i18n from 'i18next'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import type {
	DefaultConsumerInputs,
	MbfInputs,
	QuestionnaireStep,
	QuestionnaireStepValidationParameters,
} from '../types/questionnaire.types'

import { EmploymentStatus } from '../types/questionnaire.types'
import { FormikNumberInput } from './formikIntegration'
import { FormikSingleSelect } from './formikIntegration/singleSelect'

const getIsEmploymentAnnounced = (input: MbfInputs | undefined) => {
	if (input?.isEmploymentAnnounced == null) {
		return
	}
	return input.isEmploymentAnnounced ? 'true' : 'false'
}

const isEmployee = (employmentStatus?: EmploymentStatus) => {
	return employmentStatus == EmploymentStatus.Employee
}

const isSelfEmployed = (employmentStatus?: EmploymentStatus) => {
	return employmentStatus == EmploymentStatus.SelfEmployed
}

const Step = ({
	formProps,
}: {
	formProps: FormikProps<DefaultConsumerInputs>
}) => {
	const { t } = useTranslation()
	const { values, touched, setFieldValue, setSubmitting, setTouched } =
		formProps

	useEffect(() => {
		setSubmitting(false)
		setTouched({
			...touched,
			mbfInputs: undefined,
		})
	}, [])

	const employmentStatusChoices = [
		{
			value: EmploymentStatus.Employee,
			label: t('consumerInputs-mbfInputs-empolyee-label'),
		},
		{
			value: EmploymentStatus.SelfEmployed,
			label: t('consumerInputs-mbfInputs-selfEmployed-label'),
		},
		{
			value: EmploymentStatus.Pensioner,
			label: t('consumerInputs-mbfInputs-pensioner-label'),
		},
		{
			value: EmploymentStatus.Student,
			label: t('consumerInputs-mbfInputs-student-label'),
		},
	]

	useEffect(() => {
		if (values.mbfInputs?.employmentStatus != EmploymentStatus.Employee) {
			setFieldValue('mbfInputs.isEmploymentAnnounced', null, true)
		}
		if (values.mbfInputs?.employmentStatus != EmploymentStatus.SelfEmployed) {
			setFieldValue('mbfInputs.selfEmployedSince', null, true)
		}
	}, [setFieldValue, values.mbfInputs?.employmentStatus])

	const [selfEmployedSinceField, selfEmployedSinceMeta] = useField(
		'mbfInputs.selfEmployedSince',
	)

	return (
		<FlowPage.Content $spacing="xl">
			<FlowPage.Header
				title={t('consumerInputs-mbfInputs-title')}
				description={t('consumerInputs-mbfInputs-subtitle')}
			/>
			<FormikSingleSelect
				fieldName="mbfInputs.employmentStatus"
				label={t('consumerInputs-mbfInputs-employmentStatusChoice-title')}
				isRequired
				choices={employmentStatusChoices}
				selected={values.mbfInputs?.employmentStatus}
			/>
			<FormikNumberInput
				fieldName="mbfInputs.housingChangesLast5Years"
				label={t('consumerInputs-carCostStep-housingChangesLast5Years-text')}
				isRequired
				isEmptyByDefault
			/>
			{isEmployee(values.mbfInputs?.employmentStatus) && (
				<FormikSingleSelect
					fieldName="mbfInputs.isEmploymentAnnounced"
					label={t('consumerInputs-mbfInputs-announcedEmployment-title')}
					isRequired
					choicesOrientation="horizontal"
					choices={[
						{
							value: 'true',
							label: t('btn-yes'),
						},
						{
							value: 'false',
							label: t('btn-no'),
						},
					]}
					selected={getIsEmploymentAnnounced(values.mbfInputs)}
					onChange={(value) => {
						setFieldValue(
							'mbfInputs.isEmploymentAnnounced',
							value == 'true',
							true,
						)
					}}
				/>
			)}
			{isSelfEmployed(values.mbfInputs?.employmentStatus) && (
				<FormItem
					label={t('consumerInputs-carCostStep-selfEmployedSince-text')}
					isRequired
					requiredText={t('required')}
					errorText={
						selfEmployedSinceMeta.touched
							? selfEmployedSinceMeta.error
							: undefined
					}
				>
					<TextInputField
						variant="outlined"
						type="date"
						{...selfEmployedSinceField}
					/>
				</FormItem>
			)}
		</FlowPage.Content>
	)
}
const Validation = async ({
	values,
	errors,
}: QuestionnaireStepValidationParameters): Promise<
	FormikErrors<DefaultConsumerInputs>
> => {
	if (
		!values.mbfInputs?.employmentStatus ||
		values.mbfInputs.employmentStatus == EmploymentStatus.None
	) {
		errors.mbfInputs = {
			...errors.mbfInputs,
			employmentStatus: i18n.t('validation-fieldRequired'),
		}
	}

	if (values.mbfInputs?.housingChangesLast5Years == null) {
		errors.mbfInputs = {
			...errors.mbfInputs,
			housingChangesLast5Years: i18n.t('validation-fieldRequired'),
		}
	}

	if (
		isEmployee(values.mbfInputs?.employmentStatus) &&
		values.mbfInputs?.isEmploymentAnnounced == null
	) {
		errors.mbfInputs = {
			...errors.mbfInputs,
			isEmploymentAnnounced: i18n.t('validation-fieldRequired'),
		}
	}

	if (
		isSelfEmployed(values.mbfInputs?.employmentStatus) &&
		values.mbfInputs?.selfEmployedSince == null
	) {
		errors.mbfInputs = {
			...errors.mbfInputs,
			selfEmployedSince: i18n.t('validation-fieldRequired'),
		}
	}
	return errors
}
const MbfStep: QuestionnaireStep = {
	validation: Validation,
	component: Step,
}

export default MbfStep
